import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Save, X, Loader2, ChevronDown, Plus, Trash2, Undo2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ============================================================================
// Types
// ============================================================================

export interface EditableDataGridHandle {
  updateCells: (
    updates: Array<{ row: number; col: number; value: string }>
  ) => void;
  /** Returns a snapshot of all rows. Reflects state asynchronously — reads
   *  after `updateCells()` in the same handler return pre-update values
   *  until the next render. */
  getRows: () => string[][];
  /** Trigger save programmatically. */
  save: () => Promise<void>;
  /** Trigger cancel programmatically. */
  cancel: () => void;
  /** Add a blank row programmatically. */
  addRow: () => void;
  /** Undo the last row add/delete action programmatically. */
  undoRowAction: () => void;
}

interface EditableDataGridProps {
  headers: string[];
  rows: string[][];
  onSave?: (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[] }
  ) => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
  readOnlyHeaders?: boolean;
  className?: string;
  selectable?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (rows: Set<number>) => void;
  cellRenderer?: (
    rowIndex: number,
    colIndex: number,
    value: string,
    onChange: (value: string) => void,
    rowValues: string[]
  ) => React.ReactNode | null;
  /**
   * Zero-based column index pointing to the package identifier column.
   * When provided, enables package-based filtering in the select-all dropdown.
   * Out-of-range or undefined values disable package filtering.
   */
  packageColumnIndex?: number;
  /**
   * When true, automatically saves changes after a 2-second debounce period.
   * Default is false to avoid interrupting user workflow.
   * Manual save is still available via the Save Changes button.
   */
  autoSave?: boolean;
  /**
   * When true, hides the built-in Save Changes / Cancel action bar so the
   * parent can render its own controls (e.g., alongside other buttons).
   */
  hideActionBar?: boolean;
  /** Called whenever the grid's internal `hasChanges` state changes. */
  onHasChangesChange?: (hasChanges: boolean) => void;
  /** Called whenever the grid's internal `isSaving` state changes. */
  onIsSavingChange?: (isSaving: boolean) => void;
  /** Called whenever undo row action availability changes. */
  onCanUndoRowActionChange?: (canUndo: boolean) => void;
}

interface EditingCell {
  row: number;
  col: number;
  isHeader?: boolean;
}

function getTextareaMirrorValue(value: string) {
  if (!value) return " ";
  return value.endsWith("\n") ? `${value} ` : value;
}

type LastRowAction =
  | { type: "add"; rowIndex: number }
  | {
      type: "delete";
      rowIndex: number;
      originalIndex: number | null;
      row: string[];
      deletedIndexPosition: number | null;
      wasSelected: boolean;
    };

// ============================================================================
// EditableCell Component
// ============================================================================

interface EditableCellProps {
  value: string;
  isEditing: boolean;
  isModified?: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onFinishEdit: () => void;
  onNavigate: (direction: "up" | "down" | "left" | "right") => void;
  readOnly?: boolean;
}

function EditableCell({
  value,
  isEditing,
  isModified,
  onStartEdit,
  onChange,
  onFinishEdit,
  onNavigate,
  readOnly,
}: EditableCellProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(value);

  // Focus the editor when entering edit mode.
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Sync local value when prop changes (e.g., on cancel)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    switch (e.key) {
      case "Enter":
        if (!e.shiftKey) {
          e.preventDefault();
          onChange(localValue);
          onNavigate("down");
        }
        break;
      case "Tab":
        e.preventDefault();
        onChange(localValue);
        onNavigate(e.shiftKey ? "left" : "right");
        break;
      case "Escape":
        e.preventDefault();
        setLocalValue(value);
        onFinishEdit();
        break;
      case "ArrowUp":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onChange(localValue);
          onNavigate("up");
        }
        break;
      case "ArrowDown":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onChange(localValue);
          onNavigate("down");
        }
        break;
    }
  };

  const handleBlur = () => {
    onChange(localValue);
    onFinishEdit();
  };

  if (isEditing && !readOnly) {
    return (
      <div className="grid min-w-[40px] cursor-text whitespace-pre-wrap break-words rounded-sm bg-primary/10 px-1 py-1 text-[10px] leading-tight outline outline-2 outline-primary">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          rows={1}
          className="col-start-1 row-start-1 h-full w-full min-w-0 resize-none overflow-hidden border-0 bg-transparent p-0 font-[inherit] text-[10px] leading-tight text-foreground outline-none"
          aria-label="Edit cell value"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none invisible col-start-1 row-start-1 whitespace-pre-wrap break-words"
        >
          {getTextareaMirrorValue(localValue)}
        </div>
      </div>
    );
  }

  return (
    <div
        className={cn(
          "min-w-[40px] cursor-pointer whitespace-pre-wrap break-words rounded-sm px-1 py-1 text-[10px] leading-tight",
          !readOnly && "hover:bg-muted/50",
          isModified && "bg-amber-50 dark:bg-amber-950/30"
        )}
      onClick={readOnly ? undefined : onStartEdit}
      onKeyDown={(e) => {
        if (!readOnly && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onStartEdit();
        }
      }}
      tabIndex={readOnly ? -1 : 0}
      role={readOnly ? undefined : "button"}
      aria-label={readOnly ? value : `Edit cell: ${value || "empty"}`}
    >
      {value || <span className="text-muted-foreground">-</span>}
    </div>
  );
}

// ============================================================================
// EditableHeader Component
// ============================================================================

interface EditableHeaderProps {
  value: string;
  isEditing: boolean;
  isModified?: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onFinishEdit: () => void;
  onNavigate: (direction: "left" | "right") => void;
  readOnly?: boolean;
}

function EditableHeader({
  value,
  isEditing,
  isModified,
  onStartEdit,
  onChange,
  onFinishEdit,
  onNavigate,
  readOnly,
}: EditableHeaderProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    switch (e.key) {
      case "Enter":
        if (!e.shiftKey) {
          e.preventDefault();
          onChange(localValue);
          onNavigate("right");
        }
        break;
      case "Tab":
        e.preventDefault();
        onChange(localValue);
        onNavigate(e.shiftKey ? "left" : "right");
        break;
      case "Escape":
        e.preventDefault();
        setLocalValue(value);
        onFinishEdit();
        break;
    }
  };

  const handleBlur = () => {
    onChange(localValue);
    onFinishEdit();
  };

  if (isEditing && !readOnly) {
    return (
      <div className="grid min-w-[60px] cursor-text whitespace-pre-wrap break-words rounded-sm bg-primary/10 px-1 py-1 text-[10px] font-medium leading-tight outline outline-2 outline-primary">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          rows={1}
          className="col-start-1 row-start-1 h-full w-full min-w-0 resize-none overflow-hidden border-0 bg-transparent p-0 font-[inherit] text-[10px] font-medium leading-tight text-foreground outline-none"
          aria-label="Edit header value"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none invisible col-start-1 row-start-1 whitespace-pre-wrap break-words"
        >
          {getTextareaMirrorValue(localValue)}
        </div>
      </div>
    );
  }

  return (
    <div
        className={cn(
          "min-w-[60px] cursor-pointer whitespace-pre-wrap break-words rounded-sm px-1 py-1 text-[10px] font-medium leading-tight",
          !readOnly && "hover:bg-muted/50",
          isModified && "bg-amber-50 dark:bg-amber-950/30"
        )}
      onClick={readOnly ? undefined : onStartEdit}
      onKeyDown={(e) => {
        if (!readOnly && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onStartEdit();
        }
      }}
      tabIndex={readOnly ? -1 : 0}
      role={readOnly ? undefined : "button"}
      aria-label={readOnly ? value : `Edit header: ${value || "empty"}`}
      title={value}
    >
      {value || <span className="text-muted-foreground">Untitled</span>}
    </div>
  );
}

// ============================================================================
// EditableDataGrid Component
// ============================================================================

export const EditableDataGrid = forwardRef<
  EditableDataGridHandle,
  EditableDataGridProps
>(function EditableDataGrid(
  {
    headers,
    rows,
    onSave,
    onCancel,
    readOnly = false,
    readOnlyHeaders = false,
    className,
    selectable = false,
    selectedRows,
    onSelectionChange,
    cellRenderer,
    packageColumnIndex,
    autoSave = false,
    hideActionBar = false,
    onHasChangesChange,
    onIsSavingChange,
    onCanUndoRowActionChange,
  },
  ref
) {
  // Local state for editing
  const [localHeaders, setLocalHeaders] = useState<string[]>(headers);
  const [localRows, setLocalRows] = useState<string[][]>(rows);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastRowAction, setLastRowAction] = useState<LastRowAction | null>(null);
  const [deletedRowIndices, setDeletedRowIndices] = useState<number[]>([]);
  const [rowIndexMapping, setRowIndexMapping] = useState<Array<number | null>>(
    () => rows.map((_, index) => index)
  );

  // Selection tracking for shift+click range selection
  const lastCheckedIndexRef = useRef<number | null>(null);

  // Track which cells have been modified
  const [modifiedHeaders, setModifiedHeaders] = useState<Set<number>>(
    new Set()
  );
  const [modifiedCells, setModifiedCells] = useState<Set<string>>(new Set());

  // Track last saved state to prevent auto-save loops
  const lastSavedSnapshotRef = useRef<string>("");

  // Sync with external data when props change
  useEffect(() => {
    setLocalHeaders(headers);
    setLocalRows(rows);
    setModifiedHeaders(new Set());
    setModifiedCells(new Set());
    setLastRowAction(null);
    setDeletedRowIndices([]);
    setRowIndexMapping(rows.map((_, index) => index));
    lastCheckedIndexRef.current = null;
    // Update snapshot when props change (external update)
    lastSavedSnapshotRef.current = JSON.stringify({ headers, rows });
  }, [headers, rows]);

  // Check if data has changed
  const hasChanges = useMemo(() => {
    if (JSON.stringify(headers) !== JSON.stringify(localHeaders)) {
      return true;
    }
    if (JSON.stringify(rows) !== JSON.stringify(localRows)) {
      return true;
    }
    return false;
  }, [headers, rows, localHeaders, localRows]);

  // Notify parent of state changes
  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  useEffect(() => {
    onIsSavingChange?.(isSaving);
  }, [isSaving, onIsSavingChange]);

  useEffect(() => {
    onCanUndoRowActionChange?.(lastRowAction !== null);
  }, [lastRowAction, onCanUndoRowActionChange]);

  // Reset to original data
  const handleCancel = useCallback(() => {
    setLocalHeaders(headers);
    setLocalRows(rows);
    setEditingCell(null);
    setModifiedHeaders(new Set());
    setModifiedCells(new Set());
    setLastRowAction(null);
    setDeletedRowIndices([]);
    setRowIndexMapping(rows.map((_, index) => index));
    onCancel?.();
  }, [headers, rows, onCancel]);

  // Save changes
  const handleSave = useCallback(async () => {
    if (!onSave || !hasChanges) return;

    setIsSaving(true);
    try {
      await onSave(localHeaders, localRows, { deletedRowIndices });
      setModifiedHeaders(new Set());
      setModifiedCells(new Set());
      setLastRowAction(null);
      setDeletedRowIndices([]);
      setRowIndexMapping(localRows.map((_, index) => index));
    } finally {
      setIsSaving(false);
    }
  }, [onSave, hasChanges, localHeaders, localRows, deletedRowIndices]);

  // Auto-save changes after a delay
  useEffect(() => {
    if (!autoSave || !onSave || !hasChanges || isSaving) return;

    const currentSnapshot = JSON.stringify({ headers: localHeaders, rows: localRows });
    // Only auto-save if state differs from last saved snapshot
    if (currentSnapshot === lastSavedSnapshotRef.current) return;

    const timer = setTimeout(async () => {
      try {
        await handleSave();
        // Update snapshot after successful save
        lastSavedSnapshotRef.current = JSON.stringify({ headers: localHeaders, rows: localRows });
      } catch {
        // Error already handled in handleSave, don't update snapshot on failure
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [autoSave, hasChanges, isSaving, handleSave, onSave, localHeaders, localRows]);

  // Update header value
  const updateHeader = useCallback((colIndex: number, value: string) => {
    setLocalHeaders((prev) => {
      const updated = [...prev];
      updated[colIndex] = value;
      return updated;
    });
    setModifiedHeaders((prev) => new Set(prev).add(colIndex));
  }, []);

  // Update cell value
  const updateCell = useCallback(
    (rowIndex: number, colIndex: number, value: string) => {
      setLocalRows((prev) => {
        const updated = prev.map((row) => [...row]);
        updated[rowIndex][colIndex] = value;
        return updated;
      });
      setModifiedCells((prev) => new Set(prev).add(`${rowIndex}-${colIndex}`));
    },
    []
  );

  const handleAddRow = useCallback(() => {
    const newRow = Array.from({ length: localHeaders.length }, () => "");
    setLocalRows((prev) => [...prev, newRow]);
    setRowIndexMapping((prev) => [...prev, null]);
    setEditingCell({ row: localRows.length, col: 0, isHeader: false });
    setLastRowAction({ type: "add", rowIndex: localRows.length });
  }, [localHeaders.length, localRows.length]);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    const rowToDelete = localRows[rowIndex];
    if (!rowToDelete) return;
    const originalIndex = rowIndexMapping[rowIndex] ?? null;
    const wasSelected = selectedRows?.has(rowIndex) ?? false;

    setLocalRows((prev) => prev.filter((_, index) => index !== rowIndex));
    setRowIndexMapping((prev) => prev.filter((_, index) => index !== rowIndex));
    setEditingCell((current) => {
      if (!current || current.isHeader) return current;
      if (current.row === rowIndex) return null;
      if (current.row > rowIndex) return { ...current, row: current.row - 1 };
      return current;
    });
    setModifiedCells(new Set());
    setDeletedRowIndices((prev) => {
      const deletedIndexPosition =
        originalIndex === null ? null : prev.length;
      const next =
        originalIndex === null ? prev : [...prev, originalIndex];
      setLastRowAction({
        type: "delete",
        rowIndex,
        originalIndex,
        row: [...rowToDelete],
        deletedIndexPosition,
        wasSelected,
      });
      return next;
    });
    if (onSelectionChange) {
      const nextSelection = new Set<number>();
      for (const selectedRow of selectedRows ?? new Set<number>()) {
        if (selectedRow < rowIndex) nextSelection.add(selectedRow);
        if (selectedRow > rowIndex) nextSelection.add(selectedRow - 1);
      }
      onSelectionChange(nextSelection);
    }
  }, [localRows, onSelectionChange, rowIndexMapping, selectedRows]);

  const handleUndoRowAction = useCallback(() => {
    if (!lastRowAction) return;

    if (lastRowAction.type === "add") {
      setLocalRows((prev) => prev.filter((_, index) => index !== lastRowAction.rowIndex));
      setRowIndexMapping((prev) =>
        prev.filter((_, index) => index !== lastRowAction.rowIndex)
      );
      setEditingCell(null);
    } else {
      setLocalRows((prev) => {
        const next = prev.map((row) => [...row]);
        next.splice(lastRowAction.rowIndex, 0, [...lastRowAction.row]);
        return next;
      });
      setRowIndexMapping((prev) => {
        const next = [...prev];
        next.splice(lastRowAction.rowIndex, 0, lastRowAction.originalIndex);
        return next;
      });
      if (lastRowAction.deletedIndexPosition !== null) {
        setDeletedRowIndices((prev) =>
          prev.filter((_, index) => index !== lastRowAction.deletedIndexPosition)
        );
      }
      if (onSelectionChange) {
        const nextSelection = new Set<number>();
        for (const selectedRow of selectedRows ?? new Set<number>()) {
          if (selectedRow < lastRowAction.rowIndex) nextSelection.add(selectedRow);
          if (selectedRow >= lastRowAction.rowIndex) nextSelection.add(selectedRow + 1);
        }
        if (lastRowAction.wasSelected) {
          nextSelection.add(lastRowAction.rowIndex);
        }
        onSelectionChange(nextSelection);
      }
    }

    setModifiedCells(new Set());
    setLastRowAction(null);
  }, [lastRowAction, onSelectionChange, selectedRows]);

  // Expose imperative handle for bulk cell updates
  useImperativeHandle(
    ref,
    () => ({
      updateCells: (
        updates: Array<{ row: number; col: number; value: string }>
      ) => {
        setLocalRows((prev) => {
          const updated = prev.map((row) => [...row]);
          for (const { row, col, value } of updates) {
            if (updated[row]) {
              updated[row][col] = value;
            }
          }
          return updated;
        });
        setModifiedCells((prev) => {
          const next = new Set(prev);
          for (const { row, col } of updates) {
            next.add(`${row}-${col}`);
          }
          return next;
        });
      },
      /** Returns a snapshot of the current rows. Note: reflects state
       *  asynchronously — reads after `updateCells()` in the same handler
       *  will return pre-update values until the next render. */
      getRows: () => localRows.map((row) => [...row]),
      save: handleSave,
      cancel: handleCancel,
      addRow: handleAddRow,
      undoRowAction: handleUndoRowAction,
    }),
    [localRows, handleSave, handleCancel, handleAddRow, handleUndoRowAction]
  );

  // Row selection handlers
  const handleRowCheckboxChange = useCallback(
    (rowIndex: number, event: React.MouseEvent) => {
      if (!onSelectionChange) return;

      const current = selectedRows ?? new Set<number>();
      const next = new Set(current);

      if (event.shiftKey && lastCheckedIndexRef.current !== null) {
        const start = Math.min(lastCheckedIndexRef.current, rowIndex);
        const end = Math.max(lastCheckedIndexRef.current, rowIndex);
        // Apply the same state as the clicked row's intended state
        const willSelect = !current.has(rowIndex);
        for (let i = start; i <= end; i++) {
          if (willSelect) {
            next.add(i);
          } else {
            next.delete(i);
          }
        }
      } else {
        if (next.has(rowIndex)) {
          next.delete(rowIndex);
        } else {
          next.add(rowIndex);
        }
      }

      lastCheckedIndexRef.current = rowIndex;
      onSelectionChange(next);
    },
    [selectedRows, onSelectionChange]
  );

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    const current = selectedRows ?? new Set<number>();
    if (current.size === localRows.length) {
      // All selected -> deselect all
      onSelectionChange(new Set());
    } else {
      // Select all
      const all = new Set<number>();
      for (let i = 0; i < localRows.length; i++) {
        all.add(i);
      }
      onSelectionChange(all);
    }
  }, [selectedRows, onSelectionChange, localRows.length]);

  const selectAllRows = useCallback(() => {
    if (!onSelectionChange) return;
    const all = new Set<number>();
    for (let i = 0; i < localRows.length; i++) {
      all.add(i);
    }
    onSelectionChange(all);
  }, [localRows.length, onSelectionChange]);

  const clearSelection = useCallback(() => {
    onSelectionChange?.(new Set());
  }, [onSelectionChange]);

  // Compute package options for dropdown
  const packageOptions = useMemo(() => {
    if (packageColumnIndex === undefined || packageColumnIndex < 0) return [];
    
    const packages = new Set<string>();
    for (const row of localRows) {
      const pkg = row[packageColumnIndex];
      if (pkg?.trim()) {
        packages.add(pkg.trim());
      }
    }
    return Array.from(packages).sort();
  }, [localRows, packageColumnIndex]);

  const handleSelectByPackage = useCallback((packageName: string) => {
    if (!onSelectionChange || packageColumnIndex === undefined || packageColumnIndex < 0) return;

    const newSelection = new Set<number>();
    for (let i = 0; i < localRows.length; i++) {
      const row = localRows[i];
      if (row && row[packageColumnIndex]?.trim() === packageName) {
        newSelection.add(i);
      }
    }
    onSelectionChange(newSelection);
  }, [localRows, packageColumnIndex, onSelectionChange]);

  // Navigation between cells
  const navigateToCell = useCallback(
    (
      currentRow: number,
      currentCol: number,
      direction: "up" | "down" | "left" | "right",
      isHeader: boolean = false
    ) => {
      let newRow = currentRow;
      let newCol = currentCol;
      let newIsHeader = isHeader;

      switch (direction) {
        case "up":
          if (isHeader) {
            // Already at header, stay there
            return;
          }
          if (currentRow === 0) {
            // Move to header only if header editing is allowed
            if (readOnlyHeaders) {
              setEditingCell(null);
              return;
            }
            newIsHeader = true;
          } else {
            newRow = currentRow - 1;
          }
          break;
        case "down":
          if (isHeader) {
            // Move from header to first row
            newIsHeader = false;
            newRow = 0;
          } else if (currentRow < localRows.length - 1) {
            newRow = currentRow + 1;
          } else {
            // At last row, close edit
            setEditingCell(null);
            return;
          }
          break;
        case "left":
          if (currentCol > 0) {
            newCol = currentCol - 1;
          }
          break;
        case "right":
          if (currentCol < localHeaders.length - 1) {
            newCol = currentCol + 1;
          } else {
            // At last column, close edit
            setEditingCell(null);
            return;
          }
          break;
      }

      setEditingCell({ row: newRow, col: newCol, isHeader: newIsHeader });
    },
    [localHeaders.length, localRows.length, readOnlyHeaders]
  );

  // Derived selection state
  const selectionCount = selectedRows?.size ?? 0;
  const allSelected =
    selectable && localRows.length > 0 && selectionCount === localRows.length;
  const someSelected = selectable && selectionCount > 0 && !allSelected;

  // Create column definitions
  const columns: ColumnDef<string[]>[] = useMemo(() => {
    const dataColumns: ColumnDef<string[]>[] = localHeaders.map(
      (_header, colIndex) => ({
        id: `col-${colIndex}`,
        size: 120,
        header: () => (
          <EditableHeader
            value={localHeaders[colIndex]}
            isEditing={
              editingCell?.isHeader === true && editingCell?.col === colIndex
            }
            isModified={modifiedHeaders.has(colIndex)}
            onStartEdit={() =>
              setEditingCell({ row: -1, col: colIndex, isHeader: true })
            }
            onChange={(value) => updateHeader(colIndex, value)}
            onFinishEdit={() => setEditingCell(null)}
            onNavigate={(direction) =>
              navigateToCell(-1, colIndex, direction, true)
            }
            readOnly={readOnly || readOnlyHeaders}
          />
        ),
        cell: ({ row }) => {
          const cellValue = row.original[colIndex] ?? "";
          const customRenderer = cellRenderer?.(
            row.index,
            colIndex,
            cellValue,
            (value) => updateCell(row.index, colIndex, value),
            row.original
          );
          if (customRenderer !== undefined && customRenderer !== null) {
            return customRenderer;
          }
          return (
            <EditableCell
              value={cellValue}
              isEditing={
                !editingCell?.isHeader &&
                editingCell?.row === row.index &&
                editingCell?.col === colIndex
              }
              isModified={modifiedCells.has(`${row.index}-${colIndex}`)}
              onStartEdit={() =>
                setEditingCell({
                  row: row.index,
                  col: colIndex,
                  isHeader: false,
                })
              }
              onChange={(value) => updateCell(row.index, colIndex, value)}
              onFinishEdit={() => setEditingCell(null)}
              onNavigate={(direction) =>
                navigateToCell(row.index, colIndex, direction, false)
              }
              readOnly={readOnly}
            />
          );
        },
      })
    );

    const actionColumn: ColumnDef<string[]> | null = readOnly
      ? null
      : {
          id: "__actions",
          size: 56,
          header: () => <span className="sr-only">Row actions</span>,
          cell: ({ row }) => (
            <div className="flex items-center justify-center px-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={() => handleDeleteRow(row.index)}
                aria-label={`Delete row ${row.index + 1}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ),
        };

    if (!selectable) {
      return actionColumn ? [...dataColumns, actionColumn] : dataColumns;
    }

    // Checkbox column for row selection
    const checkboxColumn: ColumnDef<string[]> = {
      id: "__selection",
      size: packageOptions.length > 0 ? 60 : 40,
      header: () => (
        <div className="flex items-center justify-center px-2">
          {packageOptions.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-0.5 cursor-pointer">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                  <ChevronDown className="size-3 text-muted-foreground" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
                <DropdownMenuItem onClick={selectAllRows}>
                  Select All ({localRows.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={clearSelection}>
                  Deselect All
                </DropdownMenuItem>
                {packageOptions.map((pkg) => (
                  <DropdownMenuItem key={pkg} onClick={() => handleSelectByPackage(pkg)}>
                    Select "{pkg}"
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={handleSelectAll}
              aria-label="Select all rows"
            />
          )}
        </div>
      ),
      cell: ({ row }) => {
        const isSelected = selectedRows?.has(row.index) ?? false;
        return (
          <div className="flex items-center justify-center px-2">
            <Checkbox
              checked={isSelected}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleRowCheckboxChange(row.index, e);
              }}
              onCheckedChange={() => {
                // Handled by onClick for shift+click support
              }}
              aria-label={`Select row ${row.index + 1}`}
            />
          </div>
        );
      },
    };

    return actionColumn
      ? [checkboxColumn, ...dataColumns, actionColumn]
      : [checkboxColumn, ...dataColumns];
  }, [
    localHeaders,
    editingCell,
    modifiedHeaders,
    modifiedCells,
    updateHeader,
    updateCell,
    navigateToCell,
    readOnly,
    readOnlyHeaders,
    selectable,
    selectedRows,
    allSelected,
    someSelected,
    handleSelectAll,
    handleRowCheckboxChange,
    cellRenderer,
    packageOptions,
    handleSelectByPackage,
    selectAllRows,
    clearSelection,
    localRows.length,
    handleDeleteRow,
  ]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: localRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={cn("space-y-4", className)}>
      {(hasChanges || isSaving) && !readOnly && !hideActionBar && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleAddRow} disabled={isSaving}>
              <Plus className="size-4" />
              Add Row
            </Button>
            <Button variant="outline" size="sm" onClick={handleUndoRowAction} disabled={isSaving || !lastRowAction}>
              <Undo2 className="size-4" />
              Undo Row Action
            </Button>
          </div>
          <div className="flex items-center justify-end gap-2">
          {hasChanges && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="size-4" />
              Cancel
            </Button>
          )}
          {hasChanges ? (
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          ) : isSaving ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Saving...
            </div>
          ) : null}
          </div>
        </div>
      )}

      {!readOnly && !hasChanges && !isSaving && !hideActionBar && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleAddRow}>
            <Plus className="size-4" />
            Add Row
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndoRowAction}
            disabled={!lastRowAction}
          >
            <Undo2 className="size-4" />
            Undo Row Action
          </Button>
        </div>
      )}



      {/* Data grid - using native table elements for proper sticky header support */}
      <div className="rounded-md border overflow-auto max-h-[80vh]">
        <table className="w-full caption-bottom text-[10px]">
          <thead className="sticky top-0 z-10 bg-muted shadow-sm border-b-2">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id} 
                    className="px-2 py-1.5 align-top text-foreground h-8 text-left font-bold uppercase text-[10px] tracking-wider whitespace-nowrap"
                    style={{ 
                      width: header.getSize()
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-muted/50 data-[state=selected]:bg-muted border-b border-border transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td 
                      key={cell.id} 
                      className="px-1.5 py-1 align-top"
                      style={{ 
                        width: cell.column.getSize()
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="h-24 text-center p-3"
                >
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Keyboard hints */}
      {!readOnly && (
        <p className="text-xs text-muted-foreground">
          Click to edit. Press Tab to move right, Enter to move down, Shift+Enter
          for a new line, Escape to cancel. {autoSave && "Changes auto-save after 2 seconds."}
        </p>
      )}
    </div>
  );
});

/*
 * Usage Example:
 *
 * import { EditableDataGrid, type EditableDataGridHandle } from "@/components/editable-data-grid";
 * import { useMutation } from "convex/react";
 * import { api } from "@constructionautomation/backend";
 * import { toast } from "sonner";
 * import { useRef, useState } from "react";
 *
 * function TableEditor({ table }) {
 *   const updateTable = useMutation(api.specPilot.extractedTables.updateData);
 *   const gridRef = useRef<EditableDataGridHandle>(null);
 *   const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
 *
 *   const handleSave = async (headers: string[], rows: string[][]) => {
 *     await updateTable({ tableId: table._id, headers, rows });
 *     toast.success("Table saved successfully");
 *   };
 *
 *   // Bulk update example: set a value on all selected rows
 *   const handleBulkApply = (colIndex: number, value: string) => {
 *     gridRef.current?.updateCells(
 *       [...selectedRows].map((rowIdx) => ({ row: rowIdx, col: colIndex, value }))
 *     );
 *   };
 *
 *   return (
 *     <EditableDataGrid
 *       ref={gridRef}
 *       headers={table.headers}
 *       rows={table.rows}
 *       onSave={handleSave}
 *       selectable
 *       selectedRows={selectedRows}
 *       onSelectionChange={setSelectedRows}
 *       bulkActions={<button onClick={() => handleBulkApply(0, "N/A")}>Set N/A</button>}
 *       cellRenderer={(rowIndex, colIndex, value, onChange, rowValues) => {
 *         // Return null to use default EditableCell
 *         if (colIndex !== 2) return null;
 *         // Custom renderer for column 2
 *         return <span className="text-primary">{value}</span>;
 *       }}
 *     />
 *   );
 * }
 *
 * Accessibility Checklist:
 * - All editable cells are keyboard accessible (Tab, Enter, Escape)
 * - ARIA labels are applied to editable elements
 * - Focus management is handled when entering/exiting edit mode
 * - Modified cells have visual indication with accessible color contrast
 * - Row selection checkboxes have appropriate ARIA labels
 * - Select-all checkbox supports indeterminate state
 * - Bulk action bar includes clear selection with ARIA label
 *
 * Performance Considerations:
 * - Uses memoization for column definitions to avoid unnecessary re-renders
 * - Local state management prevents network calls on every keystroke
 * - TanStack Table provides optimized rendering for large datasets
 * - Imperative handle avoids re-rendering entire grid for bulk updates
 * - Shift+click range selection uses a ref to avoid state overhead
 */
